import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Tooltip } from '@material-ui/core';
import AtSelectPeriod from '../../../../components/AtSelectPeriod/AtSelectPeriod';
import { ObjectiveSelect } from '../../../../components/ObjectiveSelect';
import { useObjectivesList } from '../../../../hooks/useObjectivesList';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { AutoRefresh } from './AutoRefresh';
import { getObjetivoDefaultStep } from '../../utils/default-step';

/**
 * @fileoverview Barra de herramientas y filtros globales para el Panel Dinámico.
 * @module GlobalFilter
 */

/**
 * Componente que renderiza los controles globales del dashboard.
 *
 * Incluye:
 * - Selección de objetivo global.
 * - Selección de período de tiempo.
 * - Control de refresco automático.
 * - Botón para activar/desactivar el modo edición.
 *
 * @returns {React.ReactElement} La barra de herramientas de filtros.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function GlobalFilter() {
  const { t } = useTranslation();
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const {
    data: objectives,
    isLoading: isLoadingObjectiveList,
  } = useObjectivesList({ fromStorage: true });

  /**
   * Activa o desactiva el modo de edición de los paneles.
   * @param {boolean} editing - Verdadero para activar la edición.
   */
  function setIsEditing(editing) {
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING,
      payload: editing,
    });
  }

  /**
   * Manejador para actualizar el período de tiempo seleccionado.
   * Actualiza el estado global con el nuevo rango de fechas.
   * @param {Object} periodValue - Objeto con la información del período.
   */
  function changePeriodFn(periodValue) {
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_FILTER_PERIOD,
      payload: periodValue,
    });
  }

  /**
   * Cambia el objetivo globalmente para todos los widgets.
   *
   * Muestra una confirmación antes de proceder. Si se acepta:
   * 1. Actualiza el objetivo global en el estado.
   * 2. Recalcula el paso por defecto para el nuevo objetivo.
   * 3. Actualiza todos los widgets del layout actual con el nuevo objetivo y paso.
   *
   * @param {number} objetivoId - ID del objetivo seleccionado.
   */
  function setObjetivoId(objetivoId) {
    const confirmarCambioObjetivo = window.confirm(
      t('globalFilter.confirmGlobalObjectiveChange')
    );
    if (!confirmarCambioObjetivo) {
      return;
    }
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_GLOBAL_OBJECTIVE,
      payload: {
        objetivoId,
      },
    });
    const pasoId = getObjetivoDefaultStep({ objetivoId });
    const updatedLayout = state.activeLayout.map((item) => {
      return {
        ...item,
        objetivoId,
        customProperties: { ...item.customProperties, pasoId },
      };
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: updatedLayout,
    });
  }

  const {
    globalObjective: { applyToAll, objetivoId },
  } = state;

  return (
    <Box
      display="flex"
      gridGap={10}
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
    >
      <Box display="flex" style={{ width: 300, maxWidth: 300 }}>
        <ObjectiveSelect
          objetivos={objectives}
          isLoading={isLoadingObjectiveList}
          setObjetivoId={setObjetivoId}
          objetivoId={state.globalObjective.objetivoId}
        />
      </Box>
      <Box display="flex">
        <AtSelectPeriod
          changePeriodFn={changePeriodFn}
          persistInStorage={true}
          hideApplyToAll={true}
        />
      </Box>
      <Box display="flex" style={{ width: 300, maxWidth: 300 }}>
        <AutoRefresh />
      </Box>
      <Button
        onClick={() => setIsEditing(!state.isEditing)}
        variant="contained"
        color="primary"
        size="medium"
      >
        {state.isEditing
          ? t('globalFilter.closeEditorMode')
          : t('globalFilter.editDashboard')}
      </Button>
    </Box>
  );
}
